/** Value models of the fields pill (deliverable 16), pure: what applies, what is sent, what is linked. */
import type {
  DocPropsV1,
  DocTypeV1,
  FieldDefV1,
  LinkCreateV1,
  LinkTypeV1,
  LinkV1,
  PropValueV1,
} from '@codex/contracts';

/**
 * The type's live fields in order, then the live global fields (only on a canon type, A2.1 section
 * 4). Globals are found on the type's own list and on every other type's, once each.
 */
export function applicableFields(type: DocTypeV1, types: readonly DocTypeV1[]): FieldDefV1[] {
  const own = type.fields.filter((field) => field.docTypeId === type.id && !field.archived);
  if (type.library !== 'canon') return own;
  const seen = new Set<string>();
  const globals: FieldDefV1[] = [];
  for (const source of [type, ...types]) {
    for (const field of source.fields) {
      if (field.docTypeId !== null || field.archived || seen.has(field.id)) continue;
      seen.add(field.id);
      globals.push(field);
    }
  }
  return [...own, ...globals];
}

/** What an editor hands over: the text of an input, a checkbox, or the ticked options. */
export type PropInput = string | boolean | readonly string[];

/**
 * The value `docs.patchMeta` carries. A blank input clears (`null`); a number is sent as a number
 * when the trimmed text is a finite number and as the text otherwise (the server answers
 * `wrong_type` and the page shows it).
 */
export function toPropValue(field: FieldDefV1, input: PropInput): PropValueV1 {
  if (typeof input === 'boolean') return input;
  if (typeof input !== 'string') return input.length === 0 ? null : [...input];
  if (input === '') return null;
  if (field.kind === 'number') {
    const trimmed = input.trim();
    if (trimmed === '') return null;
    const number = Number(trimmed);
    return Number.isFinite(number) ? number : input;
  }
  return input;
}

/** The text an input shows for a stored value. */
export function propText(value: PropValueV1 | undefined): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return value.join(', ');
}

const byPosition = (a: LinkV1, b: LinkV1): number => {
  if (a.position === b.position) return 0;
  if (a.position === null) return 1;
  if (b.position === null) return -1;
  return a.position < b.position ? -1 : 1;
};

/** The link type a reference field reads. */
export function linkTypeOf(field: FieldDefV1, linkTypes: readonly LinkTypeV1[]) {
  return field.linkTypeId === null ? undefined : linkTypes.find((t) => t.id === field.linkTypeId);
}

/**
 * The live links a reference field shows: its link type's, `out` where the document is the
 * source, `in` where it is the target, and for a symmetric link type those with the document at
 * either end (as 1.B-1's deriver matches), in `position` order.
 */
export function refsOf(
  field: FieldDefV1,
  docId: string,
  links: readonly LinkV1[],
  linkTypes: readonly LinkTypeV1[],
): LinkV1[] {
  if (field.kind !== 'doc_ref' && field.kind !== 'doc_refs') return [];
  const linkType = linkTypeOf(field, linkTypes);
  const symmetric = linkType?.symmetric === true;
  const direction = field.config.direction;
  return links
    .filter((link) => {
      if (link.linkTypeId !== field.linkTypeId) return false;
      if (symmetric) return link.sourceId === docId || link.targetId === docId;
      return direction === 'out' ? link.sourceId === docId : link.targetId === docId;
    })
    .sort(byPosition);
}

/** The document at the other end of a link. */
export const otherEnd = (link: LinkV1, docId: string): string =>
  link.sourceId === docId ? link.targetId : link.sourceId;

/**
 * The `links.create` call for linking `otherId` through a reference field: the source whose path
 * carries it, and the body. `out` and symmetric fields link from the document, `in` fields from
 * the other page. Null when the field's link type is not among `linkTypes`.
 */
export function refWrite(
  field: FieldDefV1,
  docId: string,
  otherId: string,
  linkTypes: readonly LinkTypeV1[],
): { sourceId: string; body: LinkCreateV1 } | null {
  const linkType = linkTypeOf(field, linkTypes);
  if (!linkType || (field.kind !== 'doc_ref' && field.kind !== 'doc_refs')) return null;
  const from = linkType.symmetric || field.config.direction === 'out';
  return from
    ? { sourceId: docId, body: { linkTypeKey: linkType.key, targetId: otherId } }
    : { sourceId: otherId, body: { linkTypeKey: linkType.key, targetId: docId } };
}

/** Whether a stored value counts as filled: not null, not empty. `false` and `0` are values. */
function hasValue(value: PropValueV1 | undefined): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value !== '';
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

/** A prop that is set, or at least one reference; a `world_date` never counts. */
export function isFilled(field: FieldDefV1, props: DocPropsV1, refCount: number): boolean {
  if (field.kind === 'world_date') return false;
  if (field.kind === 'doc_ref' || field.kind === 'doc_refs') return refCount > 0;
  return hasValue(props[field.id]);
}

export function filledCount(
  fields: readonly FieldDefV1[],
  props: DocPropsV1,
  refCountOf: (field: FieldDefV1) => number,
): number {
  return fields.filter((field) => isFilled(field, props, refCountOf(field))).length;
}

/** The types a page may change to: live canon types other than its own, never `session`. */
export function retypeTargets(types: readonly DocTypeV1[], currentTypeId: string): DocTypeV1[] {
  return types.filter(
    (type) =>
      type.library === 'canon' &&
      !type.archived &&
      type.key !== 'session' &&
      type.id !== currentTypeId,
  );
}
